import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { createGymStripeApi } from '../../../services/gymStripeService';
import {
    Building2,
    Check,
    CreditCard,
    Users,
    Sparkles,
    AlertCircle,
    Clock,
    Gift,
    CalendarOff,
} from 'lucide-react';
import { useGymBilling } from '../../../contexts/GymBillingContext';
import { useTheme } from '../../../contexts/ThemeContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConfirmModal from '../../ui/ConfirmModal';
import {
    gymDarkContentPadding,
    gymDarkHeaderBar,
    gymDarkPageBg,
    gymDarkCard,
    gymContentTabActive,
    gymContentTabInactive,
} from '../gymPageTheme';
import {
    resolveCurrentSaasPlanId,
    currentPlanFeatureKeySet,
    isFeatureNewInPlan,
    planBillingInterval,
} from '../../../utils/gymBillingPlans';

function statusLabelKey(status) {
    const map = {
        trialing: 'gym.billing.statusTrialing',
        active: 'gym.billing.statusActive',
        expired: 'gym.billing.statusExpired',
        complimentary: 'gym.billing.statusComplimentary',
    };
    return map[status] || 'gym.billing.statusActive';
}

function AccountingTabs({ activeTab, onChange, t }) {
    const tabs = [
        { id: 'saas', label: t('gym.billing.tabSaaS'), icon: Building2 },
        { id: 'members', label: t('gym.billing.tabMembers'), icon: Users },
    ];
    return (
        <div className="flex flex-wrap gap-2 mt-4">
            {tabs.map(({ id, label, icon: Icon }) => (
                <button
                    key={id}
                    type="button"
                    onClick={() => onChange(id)}
                    className={`inline-flex items-center gap-2 transition-colors ${
                        activeTab === id ? gymContentTabActive : gymContentTabInactive
                    }`}
                >
                    <Icon className="h-4 w-4" />
                    {label}
                </button>
            ))}
        </div>
    );
}

function planDisplayAmount(plan) {
    const interval = planBillingInterval(plan);
    if (interval === 'year') {
        return plan.price_yearly ?? plan.price_yearly_fallback ?? plan.price_monthly_fallback;
    }
    return plan.price_monthly ?? plan.price_monthly_fallback;
}

function formatPlanPrice(plan) {
    const amount = planDisplayAmount(plan);
    if (amount == null) return '-';
    const n = Number(amount);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function formatEuroAmount(amount) {
    if (amount == null) return null;
    const n = Number(amount);
    if (Number.isNaN(n)) return null;
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function planListPriceForDisplay(plan) {
    const list = plan.list_price_monthly;
    const current = planDisplayAmount(plan);
    if (list == null || current == null) return null;
    return Number(list) > Number(current) ? list : null;
}

function planSavingsPercent(plan) {
    const list = planListPriceForDisplay(plan);
    const current = planDisplayAmount(plan);
    if (list == null || current == null) return null;
    const pct = Math.round((1 - Number(current) / Number(list)) * 100);
    return pct > 0 && pct < 100 ? pct : null;
}

function PlanPricingBlock({ plan, t }) {
    const interval = planBillingInterval(plan);
    const isYearly = interval === 'year';
    const listPrice = planListPriceForDisplay(plan);
    const savings = planSavingsPercent(plan);
    const hasPromo = listPrice != null;
    const perPeriodKey = isYearly ? 'gym.billing.perYear' : 'gym.billing.perMonth';

    return (
        <div className="mt-3">
            {isYearly && (
                <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300 border border-emerald-500/25 dark:border-emerald-400/30 px-2.5 py-0.5 text-xs font-semibold tracking-wide mb-2.5">
                    {t('gym.billing.yearlyFreeMonthsBadge')}
                </span>
            )}
            {!isYearly && hasPromo && savings != null && (
                <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300 border border-emerald-500/25 dark:border-emerald-400/30 px-2.5 py-0.5 text-xs font-semibold tracking-wide mb-2.5">
                    {t('gym.billing.savingsBadge', { percent: savings })}
                </span>
            )}
            <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
                <div className="flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
                        €{formatPlanPrice(plan)}
                    </span>
                    <span className="text-sm font-medium text-gray-500 dark:text-white/65 pb-1">
                        /{t(perPeriodKey)}
                    </span>
                </div>
                {hasPromo && (
                    <div
                        className="flex items-baseline gap-0.5 pb-1 text-gray-500 dark:text-white/50 line-through decoration-2 decoration-gray-400 dark:decoration-white/35"
                        aria-label={t('gym.billing.listPriceAria', {
                            price: formatEuroAmount(listPrice),
                            period: t(perPeriodKey),
                        })}
                    >
                        <span className="text-base sm:text-lg font-medium tabular-nums">
                            €{formatEuroAmount(listPrice)}
                        </span>
                        <span className="text-xs font-medium">
                            /{t(perPeriodKey)}
                        </span>
                    </div>
                )}
            </div>
            {isYearly && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-white/50">
                    {t('gym.billing.yearlyBillingHint')}
                </p>
            )}
            {!isYearly && hasPromo && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-white/50">
                    {t('gym.billing.promoPriceHint')}
                </p>
            )}
        </div>
    );
}

function ComplimentarySection({ billing, t, isDark, status }) {
    const planName = billing?.plan || 'monthly';
    return (
        <section
            className={`rounded-xl border p-6 sm:p-8 text-center ${
                isDark
                    ? `${gymDarkCard} border-emerald-500/30 text-white`
                    : 'bg-emerald-50 border-emerald-200 text-gray-900'
            }`}
        >
            <Gift className={`h-12 w-12 mx-auto mb-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <h2 className="text-lg font-bold">{t('gym.billing.complimentaryTitle')}</h2>
            <p className="text-sm mt-2 max-w-lg mx-auto opacity-90">{t('gym.billing.complimentaryBody')}</p>
            <div className="mt-5 inline-flex flex-col sm:flex-row gap-3 sm:gap-6 text-sm justify-center">
                <div>
                    <span className="opacity-70 block text-xs">{t('gym.billing.fieldBilling')}</span>
                    <strong>{t(statusLabelKey(status))}</strong>
                </div>
                <div>
                    <span className="opacity-70 block text-xs">{t('gym.billing.fieldPlan')}</span>
                    <strong className="capitalize">{planName}</strong>
                </div>
                <div>
                    <span className="opacity-70 block text-xs">{t('gym.billing.fieldMembers')}</span>
                    <strong>
                        {billing?.member_count ?? 0} / {billing?.max_users ?? '-'}
                    </strong>
                </div>
            </div>
            <p className="text-xs mt-5 opacity-70 max-w-md mx-auto">{t('gym.billing.complimentaryNote')}</p>
        </section>
    );
}

function SaasSection({
    billing,
    stripeEnabled,
    t,
    isDark,
    plans,
    status,
    handleSubscribe,
    checkoutLoading,
    onManageSubscription,
    portalLoading,
    onCancelRenewal,
    onResumeRenewal,
    cancelRenewalLoading,
    resumeRenewalLoading,
}) {
    const isComplimentary = billing?.is_complimentary || status === 'complimentary';
    if (isComplimentary) {
        return <ComplimentarySection billing={billing} t={t} isDark={isDark} status={status} />;
    }

    const commercialPlan = billing?.commercial_plan ?? null;
    const trialFeatureLevel = billing?.trial_feature_level ?? null;
    const currentPlanId = resolveCurrentSaasPlanId(billing);
    const currentFeatureKeys = currentPlanFeatureKeySet(plans, currentPlanId);
    const isTrialing = status === 'trialing';
    const pricesFromStripe = plans.some((p) => p.price_from_stripe);
    const cancelScheduled = billing?.subscription_cancel_at_period_end === true;
    const accessUntil = billing?.subscription_access_until_label;
    const canManageSubscription = billing?.can_manage_subscription === true;
    const stripeConfigured = billing?.stripe_configured === true;
    const stripeCheckoutEnabled = stripeEnabled;
    const hasPaidSubscription = Boolean(
        billing?.stripe_subscription_id
        || (status === 'active' && commercialPlan),
    );
    const canCancelRenewal =
        canManageSubscription && hasPaidSubscription && status === 'active' && !cancelScheduled;
    const canResumeRenewal = canManageSubscription && hasPaidSubscription && cancelScheduled;

    return (
        <>
            <section
                className={`rounded-xl border p-4 sm:p-5 ${
                    isDark
                        ? `${gymDarkCard} border-white/10 text-white`
                        : 'bg-white border-gray-200 text-gray-900'
                }`}
            >
                <h2 className="text-sm font-semibold uppercase tracking-wide opacity-80">
                    {t('gym.billing.saasSectionTitle')}
                </h2>
                <p className="text-xs mt-1 opacity-70">{t('gym.billing.saasSectionDesc')}</p>
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                    {(isTrialing || commercialPlan) && (
                        <div>
                            <span className="opacity-70">
                                {t(isTrialing ? 'gym.billing.fieldTrialFeatures' : 'gym.billing.fieldCommercialPlan')}:{' '}
                            </span>
                            <strong className="capitalize">
                                {isTrialing ? trialFeatureLevel || 'monthly' : commercialPlan}
                            </strong>
                            {isTrialing && (
                                <span className="opacity-70 text-xs ml-1"> ({t('gym.billing.notPaidYet')})</span>
                            )}
                        </div>
                    )}
                    <div>
                        <span className="opacity-70">{t('gym.billing.fieldBilling')}: </span>
                        <strong>{t(statusLabelKey(status))}</strong>
                    </div>
                    {status === 'trialing' && billing?.trial_ends_at && (
                        <div className="flex items-center gap-1.5">
                            <Clock className="h-4 w-4" />
                            <span>
                                {t('gym.billing.trialEnds', {
                                    date: new Date(billing.trial_ends_at).toLocaleDateString(),
                                    days: billing.days_left ?? 0,
                                })}
                            </span>
                        </div>
                    )}
                    <div>
                        <span className="opacity-70">{t('gym.billing.fieldMembers')}: </span>
                        <strong>
                            {billing?.member_count ?? 0} / {billing?.max_users ?? '-'}
                        </strong>
                    </div>
                </div>
                {status === 'trialing' && (
                    <p className="mt-3 text-xs flex items-start gap-2 opacity-90">
                        <Sparkles className="h-4 w-4 shrink-0 mt-0.5" />
                        {t('gym.billing.trialAccountingHint')}
                    </p>
                )}
            </section>

            {cancelScheduled && accessUntil && (
                <div
                    className={`mt-4 rounded-lg border px-4 py-3 text-sm flex gap-2 ${
                        isDark
                            ? 'border-amber-500/40 bg-amber-900/25 text-amber-100'
                            : 'border-amber-300 bg-amber-50 text-amber-950'
                    }`}
                >
                    <CalendarOff className="h-5 w-5 shrink-0 mt-0.5" />
                    <p>{t('gym.billing.cancellationScheduled', { date: accessUntil })}</p>
                </div>
            )}

            {!stripeConfigured && (
                <div className="mt-4 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-900/25 px-4 py-3 text-sm text-amber-950 dark:text-amber-100 flex gap-2">
                    <AlertCircle className="h-5 w-5 shrink-0" />
                    <p>{t('gym.billing.stripeSaasTodo')}</p>
                </div>
            )}

            {stripeConfigured && !stripeCheckoutEnabled && (
                <div className="mt-4 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-900/25 px-4 py-3 text-sm text-amber-950 dark:text-amber-100 flex gap-2">
                    <AlertCircle className="h-5 w-5 shrink-0" />
                    <p>{t('gym.billing.stripePricesMissing')}</p>
                </div>
            )}

            {canManageSubscription && (
                <>
                    <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-white/80">
                        {t('gym.billing.manageSectionTitle')}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={onManageSubscription}
                            disabled={portalLoading}
                            className="rounded-lg border border-indigo-400/50 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-900 dark:text-indigo-100 px-4 py-2 text-sm font-medium hover:bg-indigo-100 dark:hover:bg-indigo-900/50 disabled:opacity-60"
                        >
                            {portalLoading ? t('gym.billing.portalLoading') : t('gym.billing.manageSubscription')}
                        </button>
                        {canCancelRenewal && (
                            <button
                                type="button"
                                onClick={onCancelRenewal}
                                disabled={cancelRenewalLoading}
                                className="rounded-lg border border-red-300/60 bg-red-50 dark:bg-red-900/25 text-red-800 dark:text-red-100 px-4 py-2 text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-60"
                            >
                                {cancelRenewalLoading
                                    ? t('gym.billing.cancelRenewalLoading')
                                    : t('gym.billing.cancelRenewal')}
                            </button>
                        )}
                        {canResumeRenewal && (
                            <button
                                type="button"
                                onClick={onResumeRenewal}
                                disabled={resumeRenewalLoading}
                                className="rounded-lg border border-emerald-400/50 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100 px-4 py-2 text-sm font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-60"
                            >
                                {resumeRenewalLoading
                                    ? t('gym.billing.resumeRenewalLoading')
                                    : t('gym.billing.resumeRenewal')}
                            </button>
                        )}
                    </div>
                </>
            )}

            <h3 className="mt-8 mb-1 text-lg font-bold text-gray-900 dark:text-white">
                {t('gym.billing.plansTitle')}
            </h3>
            <p className="mb-4 text-xs text-gray-500 dark:text-white/55">
                {pricesFromStripe ? t('gym.billing.pricesFromStripe') : t('gym.billing.pricesFallback')}
            </p>
            <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
                {plans.map((plan) => {
                    const isPaidCurrent = commercialPlan && plan.id === commercialPlan;
                    const isTrialIncluded = isTrialing && plan.id === trialFeatureLevel;
                    const highlighted = isTrialIncluded || (isPaidCurrent && plan.highlight);
                    return (
                        <article
                            key={plan.id}
                            className={`rounded-xl border p-5 flex flex-col ${
                                highlighted
                                    ? 'border-indigo-500 ring-2 ring-indigo-500/30'
                                    : isDark
                                      ? `${gymDarkCard} border-white/10`
                                      : 'bg-white border-gray-200'
                            } ${isDark ? 'text-white' : 'text-gray-900'}`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <h4 className="text-lg font-bold">{plan.name}</h4>
                                {isPaidCurrent && (
                                    <span className="text-xs rounded-full bg-indigo-600 text-white px-2 py-0.5">
                                        {t('gym.billing.currentPlanBadge')}
                                    </span>
                                )}
                                {isTrialIncluded && !isPaidCurrent && (
                                    <span className="text-xs rounded-full bg-amber-500 text-white px-2 py-0.5">
                                        {t('gym.billing.trialIncludedBadge')}
                                    </span>
                                )}
                            </div>
                            <PlanPricingBlock plan={plan} t={t} />
                            <ul className="mt-4 space-y-2 text-sm flex-1">
                                {(plan.display_features || []).map((feature) => {
                                    const showUpgradeBadge = isFeatureNewInPlan(
                                        plan.id,
                                        feature.key,
                                        currentPlanId,
                                        currentFeatureKeys,
                                    );
                                    return (
                                        <li
                                            key={`${plan.id}-${feature.key}`}
                                            className="flex items-start gap-2 flex-wrap"
                                        >
                                            <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                                            <span className="flex-1 min-w-0">{feature.label}</span>
                                            {showUpgradeBadge && (
                                                <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200 px-2 py-0.5 shrink-0">
                                                    {t('gym.billing.featureUpgradeBadge')}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                            <button
                                type="button"
                                disabled={
                                    checkoutLoading
                                    || isPaidCurrent
                                    || !plan.checkout_available
                                }
                                onClick={() => handleSubscribe(plan)}
                                className={`mt-5 w-full rounded-lg py-2.5 text-sm font-medium ${
                                    plan.checkout_available && !isPaidCurrent && !checkoutLoading
                                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {checkoutLoading
                                    ? t('gym.billing.checkoutLoading')
                                    : isPaidCurrent
                                      ? t('gym.billing.planCurrent')
                                      : plan.checkout_available
                                        ? t('gym.billing.subscribe')
                                        : t('gym.billing.subscribeSoon')}
                            </button>
                        </article>
                    );
                })}
            </div>
        </>
    );
}

function MembersPaymentsSection({ t, isDark }) {
    return (
        <section
            className={`rounded-xl border border-dashed p-8 sm:p-10 text-center ${
                isDark
                    ? `${gymDarkCard} border-white/20 text-white`
                    : 'bg-white border-gray-300 text-gray-900'
            }`}
        >
            <Users className="h-10 w-10 mx-auto opacity-50 mb-3" />
            <p className="text-sm font-semibold">{t('gym.billing.membersTabTodoTitle')}</p>
            <p className="text-xs mt-2 max-w-md mx-auto opacity-70">{t('gym.billing.membersTabTodo')}</p>
        </section>
    );
}

export default function GymAccounting() {
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const { getAuthenticatedApi, refreshCurrentUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const gymStripe = useMemo(
        () => createGymStripeApi(getAuthenticatedApi),
        [getAuthenticatedApi],
    );
    const {
        billing,
        loading,
        stripeEnabled,
        refreshBilling,
    } = useGymBilling();
    const [activeTab, setActiveTab] = useState('saas');
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [cancelRenewalLoading, setCancelRenewalLoading] = useState(false);
    const [resumeRenewalLoading, setResumeRenewalLoading] = useState(false);
    const [showCancelRenewalConfirm, setShowCancelRenewalConfirm] = useState(false);
    const [billingNotice, setBillingNotice] = useState(null);
    const checkoutReturnHandledRef = useRef(null);

    useEffect(() => {
        const billingParam = searchParams.get('billing');
        if (!billingParam) return;

        const clearBillingParams = () => {
            const next = new URLSearchParams(searchParams);
            next.delete('billing');
            next.delete('session_id');
            setSearchParams(next, { replace: true });
        };

        if (billingParam === 'cancel') {
            setBillingNotice({ type: 'info', text: t('gym.billing.checkoutCancel') });
            clearBillingParams();
            return;
        }

        if (billingParam === 'portal_return') {
            refreshBilling();
            refreshCurrentUser();
            clearBillingParams();
            return;
        }

        if (billingParam !== 'success') {
            return;
        }

        const sessionId = searchParams.get('session_id') || '';
        const handleKey = `success:${sessionId}`;
        if (checkoutReturnHandledRef.current === handleKey) {
            return;
        }
        checkoutReturnHandledRef.current = handleKey;

        (async () => {
            setBillingNotice({ type: 'info', text: t('gym.billing.checkoutConfirming') });
            try {
                if (sessionId) {
                    const data = await gymStripe.confirmCheckout(sessionId);
                    setBillingNotice({
                        type: 'success',
                        text: data?.applied
                            ? t('gym.billing.checkoutSuccessActive')
                            : t('gym.billing.checkoutSuccess'),
                    });
                } else {
                    setBillingNotice({ type: 'success', text: t('gym.billing.checkoutSuccess') });
                }
            } catch {
                setBillingNotice({ type: 'success', text: t('gym.billing.checkoutSuccess') });
            }
            await refreshBilling();
            await refreshCurrentUser();
            clearBillingParams();
        })();
    }, [searchParams, setSearchParams, refreshBilling, refreshCurrentUser, gymStripe, t]);

    if (loading && !billing) {
        return (
            <div className={`min-h-full flex items-center justify-center ${gymDarkPageBg}`}>
                <LoadingSpinner />
            </div>
        );
    }

    const plans = billing?.plans || [];
    const status = billing?.billing_status || 'active';

    const handleSubscribe = async (plan) => {
        if (!plan?.id || !plan.checkout_available) return;
        try {
            setCheckoutLoading(true);
            const data = await gymStripe.createCheckout(plan.id);
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
                return;
            }
            setBillingNotice({ type: 'error', text: t('gym.billing.checkoutError') });
        } catch (err) {
            setBillingNotice({
                type: 'error',
                text: err.response?.data?.error || err.message || t('gym.billing.checkoutError'),
            });
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        try {
            setPortalLoading(true);
            const data = await gymStripe.openBillingPortal();
            if (data?.portal_url) {
                window.location.href = data.portal_url;
            }
        } catch (err) {
            setBillingNotice({
                type: 'error',
                text: err.response?.data?.error || err.message || t('gym.billing.portalError'),
            });
        } finally {
            setPortalLoading(false);
        }
    };

    const handleConfirmCancelRenewal = async () => {
        try {
            setCancelRenewalLoading(true);
            const data = await gymStripe.cancelSubscriptionRenewal();
            setShowCancelRenewalConfirm(false);
            const until = data?.access_until_label;
            setBillingNotice({
                type: 'success',
                text: until
                    ? t('gym.billing.cancelRenewalSuccess', { date: until })
                    : t('gym.billing.cancelRenewalSuccessGeneric'),
            });
            await refreshBilling();
        } catch (err) {
            setBillingNotice({
                type: 'error',
                text: err.response?.data?.error || err.message || t('gym.billing.cancelRenewalError'),
            });
        } finally {
            setCancelRenewalLoading(false);
        }
    };

    const handleResumeRenewal = async () => {
        try {
            setResumeRenewalLoading(true);
            await gymStripe.resumeSubscriptionRenewal();
            setBillingNotice({ type: 'success', text: t('gym.billing.resumeRenewalSuccess') });
            await refreshBilling();
        } catch (err) {
            setBillingNotice({
                type: 'error',
                text: err.response?.data?.error || err.message || t('gym.billing.resumeRenewalError'),
            });
        } finally {
            setResumeRenewalLoading(false);
        }
    };

    return (
        <div className={`min-h-full ${isDark ? gymDarkPageBg : 'bg-gray-50'}`}>
            <header className={`${gymDarkHeaderBar} px-fluid-lg py-4 sm:px-fluid-xl`}>
                <div className="flex items-center gap-3">
                    <CreditCard className="h-7 w-7 text-white shrink-0" />
                    <div>
                        <h1 className="text-xl font-bold text-white">
                            {t('gym.billing.accountingTitle')}
                        </h1>
                        <p className="text-sm text-white/80 mt-0.5">
                            {t('gym.billing.accountingSubtitleTwoParts')}
                        </p>
                    </div>
                </div>
            </header>

            <div className={gymDarkContentPadding}>
                <AccountingTabs activeTab={activeTab} onChange={setActiveTab} t={t} />

                {billingNotice && (
                    <div
                        className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                            billingNotice.type === 'success'
                                ? 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100'
                                : billingNotice.type === 'error'
                                  ? 'border-red-400 bg-red-50 text-red-900 dark:bg-red-900/30 dark:text-red-100'
                                  : 'border-gray-300 bg-gray-50 text-gray-800 dark:bg-white/10 dark:text-white'
                        }`}
                    >
                        {billingNotice.text}
                    </div>
                )}

                <div className="mt-6">
                    {activeTab === 'saas' ? (
                        <SaasSection
                            billing={billing}
                            stripeEnabled={stripeEnabled}
                            t={t}
                            isDark={isDark}
                            plans={plans}
                            status={status}
                            handleSubscribe={handleSubscribe}
                            checkoutLoading={checkoutLoading}
                            onManageSubscription={handleManageSubscription}
                            portalLoading={portalLoading}
                            onCancelRenewal={() => setShowCancelRenewalConfirm(true)}
                            onResumeRenewal={handleResumeRenewal}
                            cancelRenewalLoading={cancelRenewalLoading}
                            resumeRenewalLoading={resumeRenewalLoading}
                        />
                    ) : (
                        <MembersPaymentsSection t={t} isDark={isDark} />
                    )}
                </div>

                {status !== 'complimentary' && !billing?.is_complimentary && (
                    <p className="mt-8 text-xs text-gray-500 dark:text-white/50 text-center">
                        {t('gym.billing.footerNote')}
                    </p>
                )}
            </div>

            <ConfirmModal
                isOpen={showCancelRenewalConfirm}
                onClose={() => !cancelRenewalLoading && setShowCancelRenewalConfirm(false)}
                onConfirm={handleConfirmCancelRenewal}
                title={t('gym.billing.cancelRenewalConfirmTitle')}
                message={t('gym.billing.cancelRenewalConfirmBody')}
                confirmText={t('gym.billing.cancelRenewal')}
                cancelText={t('common.cancel', 'Annulla')}
                variant="warning"
                loading={cancelRenewalLoading}
            />
        </div>
    );
}
